import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Pagination, Paper, Stack, Typography } from "@mui/material";
import Logger from "../../../services/Logger";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import CrossService from "../crossword/CrossService";
import GridService from "../grid/GridService";
import SlidingPlayService from "../sliding/SlidingPlayService";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import SlidingCard from "../../browse-sliding/SlidingCard";
import BrowseStylePuzzleCard, { BROWSE_SHELF_CARD_WIDTH } from "../../browse-puzzles/BrowseStylePuzzleCard";
import { sortPuzzlesFreeFirst } from "../common/utils/PuzzleCardHelpers";
import JigsawLibraryCard from "../jigsaw/JigsawLibraryCard";
import { slidingPlayRoToCardPuzzle, pickSlidingPlayImageUrl, getSlidingStoryTitle } from "../../browse-sliding/slidingBrowseUtils";
import { jigsawPlayRoToCardPuzzle } from "../jigsaw/jigsawBrowseUtils";
import JigsawPlayService from "../jigsaw/JigsawPlayService";
import { usePagesTranslation } from "../../locales";
import { browseViewButtonSx } from "../../browse-jigsaw/JigsawBrowseStoryCard";
import { NOTICE, TEXT } from "../../../dark-theme/palette";
import WordWheelService, { wordWheelPlayRoToCard } from "../wordwheel/WordWheelService";
import { useNavigate } from "react-router-dom";

const PUZZLE_TYPES = [
    { id: 'JIGSAW', label: 'Jigsaw', service: null },
    { id: 'SLIDING', label: 'Sliding', service: null },
    { id: 'CROSSWORD', label: 'Crossword', service: CrossService },
    { id: 'HIDDEN_MESSAGE', label: 'Hidden Message', service: GridService },
    { id: 'WORD_SEARCH', label: 'Word Search', service: GridService },
    { id: 'WORD_WHEEL', label: 'Word Wheel', service: null },
];

const InProgressPanel = ({
    contentLanguage,
    onPuzzleSelect,
    onUpgradeRequired,
    activeSubTab = 0,
}) => {
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();
    const activePuzzleType = PUZZLE_TYPES[activeSubTab] || PUZZLE_TYPES[0];
    const [puzzles, setPuzzles] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, totalPages: 0, totalElements: 0 });
    const [loading, setLoading] = useState(false);
    const [userPayPlan, setUserPayPlan] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const viewerIsGuestRef = useRef(true);
    const pageSize = 10;

    // Resolve user info once
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const userData = await UserService.getUserInfo();
                if (cancelled) return;
                if (userData?.cloudUser) {
                    viewerIsGuestRef.current = false;
                    setUserPayPlan(UtilService.resolveWebPayPlan(userData.cloudUser));
                    const currSub = userData.cloudUser.currSub || {};
                    setSubscriptionStatus(currSub.status || null);
                } else {
                    setUserPayPlan(null);
                    setSubscriptionStatus(null);
                }
            } catch (error) {
                if (cancelled) return;
                Logger.error("Error getting user:", error);
                setUserPayPlan(null);
                setSubscriptionStatus(null);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Load puzzles for the active sub-tab
    useEffect(() => {
        let cancelled = false;
        const loadPuzzles = async (pageIndex = 0) => {
            setLoading(true);
            try {
                let raw = [];
                let tp = 0;
                let te = 0;
                let pageNum = pageIndex;

                if (activePuzzleType.id === "SLIDING") {
                    const response = await SlidingPlayService.listSlidingPlay({
                        page: pageIndex,
                        pageSize,
                        sort: "updatedAt:desc",
                        isCompleted: false,
                    });
                    raw = (response.content || []).map(slidingPlayRoToCardPuzzle).filter(Boolean);
                    tp = response.totalPages ?? 0;
                    te = response.totalElements ?? 0;
                    pageNum = response.number ?? pageIndex;
                } else if (activePuzzleType.id === "JIGSAW") {
                    const result = await JigsawPlayService.list({
                        page: pageIndex,
                        pageSize,
                        sort: "updatedAt:desc",
                        isCompleted: false,
                    });
                    if (result.success) {
                        raw = (result.data.content || []).map(jigsawPlayRoToCardPuzzle).filter(Boolean);
                        tp = result.data.totalPages ?? 0;
                        te = result.data.totalElements ?? 0;
                        pageNum = result.data.number ?? pageIndex;
                    }
                } else if (activePuzzleType.id === "WORD_WHEEL") {
                    const response = await WordWheelService.listDailyPlays({
                        page: pageIndex,
                        pageSize,
                        sort: "updatedAt:desc",
                        isCompleted: false,
                    });
                    raw = (response.content || []).map(wordWheelPlayRoToCard).filter(Boolean);
                    tp = response.totalPages ?? 0;
                    te = response.totalElements ?? 0;
                    pageNum = response.number ?? pageIndex;
                } else {
                    const service = activePuzzleType.service;
                    const response = await service.requestPlayablePuzzles(
                        activePuzzleType.id,
                        pageIndex,
                        pageSize,
                        false,
                        false,
                        undefined,
                        'all',
                        false
                    );
                    raw = response.content || [];
                    const p = response.page;
                    tp = p?.totalPages ?? 0;
                    te = p?.totalElements ?? 0;
                    pageNum = p?.number ?? response.number ?? pageIndex;
                }

                if (!cancelled) {
                    const ordered = viewerIsGuestRef.current ? sortPuzzlesFreeFirst(raw) : raw;
                    setPuzzles(ordered);
                    setPagination({ page: pageNum, totalPages: tp, totalElements: te });
                }
            } catch (error) {
                Logger.error(`Error fetching ${activePuzzleType.label} puzzles:`, error);
                if (!cancelled) {
                    setPuzzles([]);
                    setPagination({ page: 0, totalPages: 0, totalElements: 0 });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadPuzzles(0);
        return () => { cancelled = true; };
    }, [activePuzzleType]);

    const handlePageChange = async (page) => {
        const pageIndex = page - 1;
        setLoading(true);
        try {
            let raw = [];
            let tp = 0;
            let te = 0;
            let pageNum = pageIndex;

            if (activePuzzleType.id === "SLIDING") {
                const response = await SlidingPlayService.listSlidingPlay({
                    page: pageIndex,
                    pageSize,
                    sort: "updatedAt:desc",
                    isCompleted: false,
                });
                raw = (response.content || []).map(slidingPlayRoToCardPuzzle).filter(Boolean);
                tp = response.totalPages ?? 0;
                te = response.totalElements ?? 0;
                pageNum = response.number ?? pageIndex;
            } else if (activePuzzleType.id === "JIGSAW") {
                const result = await JigsawPlayService.list({
                    page: pageIndex,
                    pageSize,
                    sort: "updatedAt:desc",
                    isCompleted: false,
                });
                if (result.success) {
                    raw = (result.data.content || []).map(jigsawPlayRoToCardPuzzle).filter(Boolean);
                    tp = result.data.totalPages ?? 0;
                    te = result.data.totalElements ?? 0;
                    pageNum = result.data.number ?? pageIndex;
                }
            } else if (activePuzzleType.id === "WORD_WHEEL") {
                const response = await WordWheelService.listDailyPlays({
                    page: pageIndex,
                    pageSize,
                    sort: "updatedAt:desc",
                    isCompleted: false,
                });
                raw = (response.content || []).map(wordWheelPlayRoToCard).filter(Boolean);
                tp = response.totalPages ?? 0;
                te = response.totalElements ?? 0;
                pageNum = response.number ?? pageIndex;
            } else {
                const service = activePuzzleType.service;
                const response = await service.requestPlayablePuzzles(
                    activePuzzleType.id,
                    pageIndex,
                    pageSize,
                    false,
                    false,
                    undefined,
                    'all',
                    false
                );
                raw = response.content || [];
                const p = response.page;
                tp = p?.totalPages ?? 0;
                te = p?.totalElements ?? 0;
                pageNum = p?.number ?? response.number ?? pageIndex;
            }

            const ordered = viewerIsGuestRef.current ? sortPuzzlesFreeFirst(raw) : raw;
            setPuzzles(ordered);
            setPagination({ page: pageNum, totalPages: tp, totalElements: te });
        } catch (error) {
            Logger.error(`Error fetching ${activePuzzleType.label} puzzles:`, error);
        } finally {
            setLoading(false);
        }
    };

    const hasStandardPlan = UtilService.isStandardPayPlan(userPayPlan);

    const handlePuzzleClick = (puzzle) => {
        if (activePuzzleType.id === "WORD_WHEEL" && puzzle?.id) {
            navigate(`/puzzle/play-word-wheel/${encodeURIComponent(puzzle.id)}?mode=daily`);
            return;
        }
        // For free puzzles, allow access without login
        if (!puzzle.isPremium) {
            onPuzzleSelect(puzzle);
            return;
        }

        // For paid puzzles, check if user is logged in
        if (!userPayPlan) {
            // Redirect to login page if not logged in
            // This will be handled by the parent component
            onUpgradeRequired('puzzle');
            return;
        }

        // If logged in but paid puzzle and no Standard plan
        if (puzzle.isPremium && !hasStandardPlan) {
            onUpgradeRequired('puzzle');
            return;
        }

        // If logged in and has access to puzzle
        onPuzzleSelect(puzzle);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {t("library.loading-type-puzzles", { type: t(`library.tab-${activePuzzleType.id.toLowerCase().replace('_', '-')}`) })}
                    </Typography>
                </Paper>
            ) : puzzles.length > 0 ? (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "stretch",
                        }}
                    >
                        {puzzles.map((puzzle, index) => {
                            const category = activePuzzleType.id;
                            const isJigsaw = category === "JIGSAW";
                            const isSliding = category === "SLIDING";
                            const isWordWheel = category === "WORD_WHEEL";

                            return (
                                <Box
                                    key={puzzle.id || index}
                                    sx={{
                                        width: BROWSE_SHELF_CARD_WIDTH,
                                        flexShrink: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    {isJigsaw ? (
                                        <Box sx={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}>
                                            <JigsawLibraryCard
                                                puzzle={puzzle}
                                                onPlayClick={handlePuzzleClick}
                                                buttonText={t("puzzle.card.continue")}
                                                lang={lang}
                                                t={t}
                                                isInProgress
                                            />
                                        </Box>
                                    ) : isSliding ? (
                                        <SlidingCard
                                            puzzle={puzzle}
                                            userPayPlan={userPayPlan || (hasStandardPlan ? "STANDARD" : "FREE")}
                                            subscriptionStatus={subscriptionStatus}
                                            onPlayClick={puzzle.disabled ? undefined : handlePuzzleClick}
                                            buttonText={t("puzzle.card.continue")}
                                            isInProgress={true}
                                            forceUnlock={true}
                                            hideTierBadges
                                            hideWordCountChip
                                            browseStoriesChrome
                                            hideClusteredBubbles
                                            actionButtonSx={browseViewButtonSx}
                                            primaryActionReplacement={puzzle.disabled ? (
                                                <Button size="small" variant="contained" disabled sx={browseViewButtonSx}>
                                                    {t("puzzle.card.continue")}
                                                </Button>
                                            ) : undefined}
                                            belowDifficultySlot={
                                                <Box sx={{ width: "100%" }}>
                                                    {puzzle.title && (
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.35,
                                                                mb: 0.75,
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {puzzle.title}
                                                        </Typography>
                                                    )}
                                                    {puzzle.gridSize && puzzle.story ? (
                                                        <Box sx={{ width: "100%", aspectRatio: "1 / 1", overflow: "hidden", borderRadius: 1, pointerEvents: "none", bgcolor: "grey.900" }}>
                                                            <SlidingPlayMain
                                                                imageUrl={pickSlidingPlayImageUrl(puzzle.story)}
                                                                gridSize={puzzle.gridSize}
                                                                startingLayout={puzzle.slidingPlay?.currentLayout ?? puzzle.startingLayout}
                                                                hideCompletedCaption
                                                                fillParent
                                                                readOnly
                                                            />
                                                        </Box>
                                                    ) : null}
                                                </Box>
                                            }
                                        />
                                    ) : isWordWheel ? (
                                        <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                                            <Typography sx={{ fontWeight: 700 }}>{puzzle.title}</Typography>
                                            <Button
                                                variant="contained"
                                                onClick={() => handlePuzzleClick(puzzle)}
                                                sx={{ ...browseViewButtonSx, mt: "auto" }}
                                            >
                                                {t("puzzle.card.continue")}
                                            </Button>
                                        </Paper>
                                    ) : (
                                        <BrowseStylePuzzleCard
                                            category={category}
                                            puzzle={puzzle}
                                            storyTeaserRow={puzzle}
                                            contentLanguage={contentLanguage}
                                            lang={lang}
                                            t={t}
                                            userPayPlan={userPayPlan || (hasStandardPlan ? "STANDARD" : "FREE")}
                                            subscriptionStatus={subscriptionStatus}
                                            onPlayClick={handlePuzzleClick}
                                            buttonText={t("puzzle.card.continue")}
                                            isInProgress={true}
                                            forceUnlock={true}
                                            hideClusteredBubbles
                                            actionButtonSx={browseViewButtonSx}
                                            teaserIconSx={{ color: NOTICE.info.text }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {pagination.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                            <Stack spacing={2} alignItems="center">
                                <Pagination
                                    count={pagination.totalPages}
                                    page={pagination.page + 1}
                                    onChange={(_event, page) => handlePageChange(page)}
                                    size="medium"
                                    sx={{
                                        "& .MuiPaginationItem-root.Mui-selected": {
                                            backgroundColor: "rgba(56, 132, 255, 0.34)",
                                            color: TEXT.primary,
                                            "&:hover": { backgroundColor: "rgba(56, 132, 255, 0.48)" },
                                        },
                                    }}
                                    showFirstButton
                                    showLastButton
                                />
                            </Stack>
                        </Box>
                    )}
                </>
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {t("library.no-type-in-progress", { type: t(`library.tab-${activePuzzleType.id.toLowerCase().replace('_', '-')}`) })}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default InProgressPanel;

